import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Typeahead } from 'react-bootstrap-typeahead';
import get from 'lodash/get';
import { TekionEntityType } from '@upstart/harmony/lib/legos/dmsTypes';
import { areTekionEntitiesEqual } from '@upstart/harmony/lib/dms-utils';
import DTETypeahead from 'components/DTETypeahead/DTETypeahead';
import TekionTypeahead from 'components/TekionTypeahead/TekionTypeahead';
import { useAsyncCallback } from 'react-async-hook';
import {
  isDealerTrackDMSConfigured,
  isTekionDMSConfigured,
} from '../../util/dmsUtils';
import TekionEntitiesService from '../../services/TekionEntitiesService';

function AccessoryConfig({
  accessory,
  accessoryConfig,
  setAccessory,
  dealer,
  dtEntities,
}) {
  const categories = (accessoryConfig || {}).categories || [];

  const asyncGetTekionEntities = useAsyncCallback(
    TekionEntitiesService.getDealerTekionEntities,
  );
  const [tekionEntities, setTekionEntities] = useState(null);

  useEffect(() => {
    const pullTekionEntities = async () => {
      const entities = await asyncGetTekionEntities.execute(dealer?.id);
      setTekionEntities(entities);
    };
    if (isTekionDMSConfigured(dealer)) {
      pullTekionEntities();
    }
  }, []);

  const onChange = event => {
    const { name, value } = event.target;
    setAccessory({ ...accessory, [name]: value });
  };

  const onCheck = event => {
    const { name, checked } = event.target;
    setAccessory({ ...accessory, [name]: checked });
  };

  const renderDMSInputs = () => {
    // if dealertrack or tekion dms is enabled, render the corresponding entity typeahead
    // else render the normal DMS code input
    if (isDealerTrackDMSConfigured(dealer)) {
      return (
        <div className="form-group col-md-6">
          <label>DealerTrack Match</label>
          <DTETypeahead
            dtEntities={dtEntities?.filter(
              dte =>
                dte.category === 'Add to Cost' ||
                dte.category === 'Aftermarket Option' ||
                dte.category === 'Accessory',
            )}
            selectedDTE={dtEntities.find(
              dte => dte.id === accessory?.dtEntityId,
            )}
            onChange={selected => {
              const dtEntityId = get(
                selected.map(dte => dte.id),
                '[0]',
                null,
              );
              setAccessory({ ...accessory, dtEntityId });
            }}
          />
        </div>
      );
    }

    if (isTekionDMSConfigured(dealer)) {
      return (
        <div className="form-group col-md-6">
          <label>Tekion Match</label>
          <TekionTypeahead
            tekionEntities={tekionEntities?.filter(
              te => te.type === TekionEntityType.ACCESSORY,
            )}
            selected={
              tekionEntities?.find(e =>
                areTekionEntitiesEqual(e, accessory?.tekionEntity),
              ) ?? accessory?.tekionEntity
            }
            onChange={([selected]) => {
              setAccessory({ ...accessory, tekionEntity: selected });
            }}
          />
        </div>
      );
    }

    return (
      <div className="form-group col-md-3">
        <label>DMS Code</label>
        <input
          name="dmsCode"
          className="form-control"
          onChange={onChange}
          value={accessory.dmsCode || ''}
        />
      </div>
    );
  };

  return (
    <div>
      <h4>
        {accessory && accessory.id ? `Edit Accessory` : 'Add New Accessory'}
      </h4>
      <div className="row">
        <div className="form-group col-md-3">
          <label>Name*</label>
          <input
            name="name"
            className="form-control"
            onChange={onChange}
            value={accessory.name || ''}
            required
          />
        </div>
        <div className="form-group col-md-3">
          <label>Part Number*</label>
          <input
            name="partNumber"
            className="form-control"
            onChange={onChange}
            value={accessory.partNumber || ''}
            required
          />
        </div>
        <div className="form-group col-md-3">
          <label>Categories</label>
          <Typeahead
            id="accessory-typeahead"
            className="picker"
            // https://github.com/ericgio/react-bootstrap-typeahead/blob/main/docs/Upgrading.md#v5
            inputProps={{
              shouldSelectHint: (shouldSelect, event) =>
                event.key === 'Enter' || shouldSelect,
            }}
            multiple
            clearButton
            options={categories}
            selected={categories.filter(c =>
              (accessory.categoryIds || []).includes(c.id),
            )}
            labelKey="name"
            onChange={selected => {
              const categoryIds = selected.map(c => c.id);
              setAccessory({ ...accessory, categoryIds });
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-3">
          <label>Price*</label>
          <input
            name="price"
            type="number"
            className="form-control"
            onChange={onChange}
            value={accessory.price ?? ''}
            pattern="\d+"
            step="0.01"
            min="0"
            required
          />
        </div>
        <div className="form-group col-md-3">
          <label>Discount</label>
          <input
            name="discount"
            type="number"
            className="form-control"
            onChange={onChange}
            value={accessory.discount ?? ''}
            pattern="\d+"
            step="0.01"
            min="0"
            max="100"
          />
          <i>Percentage of price</i>
        </div>
        <div className="checkbox col-md-2">
          <label>
            <input
              type="checkbox"
              name="enabled"
              checked={accessory.enabled || false}
              onChange={onCheck}
            />
            Enable Accessory
          </label>
        </div>
        <div className="checkbox col-md-2">
          <label>
            <input
              type="checkbox"
              name="taxed"
              checked={accessory.taxed || false}
              onChange={onCheck}
            />
            Taxable
          </label>
        </div>
      </div>
      <div className="row">{renderDMSInputs()}</div>
      <div className="row">
        <div className="form-group col-md-10">
          <label>Description</label>
          <textarea
            rows="3"
            name="description"
            className="form-control"
            onChange={onChange}
            value={accessory.description || ''}
          />
        </div>
      </div>
    </div>
  );
}

AccessoryConfig.propTypes = {
  accessory: PropTypes.object,
  accessoryConfig: PropTypes.object,
  setAccessory: PropTypes.func,
  dealer: PropTypes.object,
  dtEntities: PropTypes.array,
};

export default AccessoryConfig;
